import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// --- TYPES ---
export type UserRole = 'investor' | 'issuer' | 'admin';
export type KycStatus = 'none' | 'pending' | 'verified' | 'rejected' | 'frozen';
export type Currency = 'USD' | 'USDC' | 'EUR';
export type AssetType = 'crypto' | 'real-estate' | 'bond' | 'commodity' | 'art' | 'rwa';
export type RiskRating = 1 | 2 | 3 | 4 | 5;

export interface Wallet {
    address: string;
    custodial: boolean;
    balances: Record<Currency, number>;
    pending: Record<Currency, number>;
}

export interface User {
    id: string;
    name: string;
    email: string;
    phone?: string;
    role: UserRole;
    kycStatus: KycStatus;
    wallet?: Wallet;
    avatar?: string;
    joinedAt: string;
    portfolio?: PortfolioItem[];
    portfolioHistory?: { date: string; value: number }[];
    preferences?: {
        notifications: boolean;
        twoFactorEnabled: boolean;
        language: string;
        timezone: string;
    };
}

// Enhanced Asset with rich metadata
export interface Asset {
    id: string;
    name: string;
    symbol: string;
    type: AssetType;
    price: number;
    change24h: number;
    // Rich metadata
    description: string;
    images: string[];
    apy?: number; // Annual Percentage Yield
    riskRating: RiskRating;
    term?: string; // e.g., "12 months", "Perpetual"
    minInvestment: number;
    documents: { name: string; type: string; url: string }[];
    location?: { address: string; city: string; country: string; coordinates?: [number, number] };
    // Fund metrics
    targetAmount?: number;
    fundedAmount?: number;
    investorCount: number;
    issuer: { name: string; verified: boolean };
    // Timestamps
    launchDate: string;
    maturityDate?: string;
    // Tags for filtering
    tags: string[];
}

export interface Transaction {
    id: string;
    userId: string;
    type: 'deposit' | 'withdrawal' | 'invest' | 'divest' | 'distribution' | 'fee';
    assetId?: string;
    assetName?: string;
    amount: number;
    currency: Currency;
    timestamp: string;
    status: 'pending' | 'completed' | 'failed';
    description?: string;
    txHash?: string; // Mock blockchain hash
}

export interface PortfolioItem {
    assetId: string;
    amount: number;
    avgCost: number;
    purchaseDate: string;
}

export interface Notification {
    id: string;
    type: 'system' | 'kyc' | 'transaction' | 'price-alert' | 'dividend' | 'security';
    title: string;
    message: string;
    timestamp: string;
    read: boolean;
    actionUrl?: string;
}

interface RWAStore {
    // State
    isAuthenticated: boolean;
    currentUser: User | null;
    assets: Asset[];
    users: User[];
    transactions: Transaction[];
    notifications: Notification[];
    selectedAssetId: string | null;

    // Auth Actions
    login: (email: string) => void;
    register: (name: string, email: string, role: UserRole) => void;
    logout: () => void;

    // Identity Actions
    submitKyc: () => void;
    approveKyc: (userId: string) => void;
    freezeUser: (userId: string) => void;
    unfreezeUser: (userId: string) => void;

    // Wallet Actions
    deposit: (amount: number, currency: Currency) => void;
    withdraw: (amount: number, currency: Currency) => void;

    // Asset Actions
    addAsset: (asset: Asset) => void;
    deleteAsset: (id: string) => void;
    updatePortfolio: (assetId: string, amount: number, cost: number) => void;
    distributeDividends: (assetId: string, totalAmount: number) => void;
    selectAsset: (id: string | null) => void;

    // Notification Actions
    addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
    markNotificationRead: (id: string) => void;
    markAllNotificationsRead: () => void;
    clearNotifications: () => void;

    // Dev Tools
    switchUser: (userId: string) => void;
    resetDemo: () => void;
}

// --- RICH MOCK ASSETS ---
const INITIAL_ASSETS: Asset[] = [
    {
        id: 'btc-1',
        name: 'Bitcoin',
        symbol: 'BTC',
        type: 'crypto',
        price: 64230.50,
        change24h: 2.4,
        description: 'Bitcoin is a decentralized digital currency, without a central bank or single administrator, that can be sent from user to user on the peer-to-peer bitcoin network.',
        images: ['https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=800'],
        riskRating: 4,
        minInvestment: 100,
        documents: [],
        investorCount: 45892,
        issuer: { name: 'Bitcoin Network', verified: true },
        launchDate: '2009-01-03',
        tags: ['crypto', 'store-of-value', 'decentralized']
    },
    {
        id: 'eth-1',
        name: 'Ethereum',
        symbol: 'ETH',
        type: 'crypto',
        price: 3450.20,
        change24h: -1.2,
        description: 'Ethereum is a decentralized, open-source blockchain featuring smart contract functionality. Ether is the native cryptocurrency of the platform.',
        images: ['https://images.unsplash.com/photo-1622630998477-20aa696ecb05?w=800'],
        riskRating: 3,
        minInvestment: 50,
        documents: [],
        investorCount: 38291,
        issuer: { name: 'Ethereum Foundation', verified: true },
        launchDate: '2015-07-30',
        tags: ['crypto', 'smart-contracts', 'defi']
    },
    {
        id: 'nyc-re-1',
        name: 'Manhattan Prime Office',
        symbol: 'NYC-RE',
        type: 'real-estate',
        price: 1050.00,
        change24h: 0.05,
        description: 'Fractional ownership in a Class-A office tower located in Midtown Manhattan. The property features 42 floors with premium tenants including Fortune 500 companies. Generates stable quarterly dividends from lease income.',
        images: [
            'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800',
            'https://images.unsplash.com/photo-1449157291145-7efd050a4d0e?w=800',
            'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800'
        ],
        apy: 7.2,
        riskRating: 2,
        term: '5 years',
        minInvestment: 500,
        documents: [
            { name: 'Investment Prospectus', type: 'pdf', url: '#' },
            { name: 'Property Valuation Report', type: 'pdf', url: '#' },
            { name: 'Tenant Schedule', type: 'pdf', url: '#' }
        ],
        location: {
            address: '450 Park Avenue',
            city: 'New York',
            country: 'United States',
            coordinates: [40.7614, -73.9718]
        },
        targetAmount: 50000000,
        fundedAmount: 42500000,
        investorCount: 1247,
        issuer: { name: 'Manhattan Capital Partners', verified: true },
        launchDate: '2024-01-15',
        maturityDate: '2029-01-15',
        tags: ['real-estate', 'commercial', 'dividend', 'institutional']
    },
    {
        id: 'sgb-1',
        name: 'Sovereign Gold Bond 2024',
        symbol: 'SGB-24',
        type: 'commodity',
        price: 54.20,
        change24h: 0.1,
        description: 'Government-backed gold bonds offering exposure to gold prices with additional interest payments. These bonds are denominated in grams of gold and provide a safe-haven investment with sovereign guarantee.',
        images: ['https://images.unsplash.com/photo-1610375461246-83df859d849d?w=800'],
        apy: 2.5,
        riskRating: 1,
        term: '8 years',
        minInvestment: 100,
        documents: [
            { name: 'Bond Prospectus', type: 'pdf', url: '#' },
            { name: 'Government Guarantee Certificate', type: 'pdf', url: '#' }
        ],
        targetAmount: 10000000,
        fundedAmount: 10000000,
        investorCount: 8934,
        issuer: { name: 'Reserve Bank', verified: true },
        launchDate: '2024-03-01',
        maturityDate: '2032-03-01',
        tags: ['commodity', 'gold', 'government', 'safe-haven']
    },
    {
        id: 'miami-re-1',
        name: 'Miami Beachfront Residences',
        symbol: 'MIA-LUX',
        type: 'real-estate',
        price: 875.00,
        change24h: 0.32,
        description: 'Luxury beachfront condominium development in Miami Beach. Features 120 units with ocean views, private beach access, and resort-style amenities. Strong rental income potential from seasonal tourism.',
        images: [
            'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800',
            'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800'
        ],
        apy: 8.5,
        riskRating: 3,
        term: '3 years',
        minInvestment: 750,
        documents: [
            { name: 'Development Prospectus', type: 'pdf', url: '#' },
            { name: 'Architectural Plans', type: 'pdf', url: '#' }
        ],
        location: {
            address: '1000 Ocean Drive',
            city: 'Miami Beach',
            country: 'United States',
            coordinates: [25.7825, -80.1323]
        },
        targetAmount: 25000000,
        fundedAmount: 18750000,
        investorCount: 892,
        issuer: { name: 'Suncoast Development LLC', verified: true },
        launchDate: '2024-06-01',
        maturityDate: '2027-06-01',
        tags: ['real-estate', 'residential', 'luxury', 'development']
    },
    {
        id: 'corp-bond-1',
        name: 'TechGiant Corporate Bond',
        symbol: 'TECH-B',
        type: 'bond',
        price: 100.00,
        change24h: 0.02,
        description: 'Investment-grade corporate bonds issued by a leading technology company. Offers stable semi-annual coupon payments with strong credit rating. Ideal for income-focused portfolios.',
        images: ['https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800'],
        apy: 5.75,
        riskRating: 2,
        term: '10 years',
        minInvestment: 1000,
        documents: [
            { name: 'Bond Indenture', type: 'pdf', url: '#' },
            { name: 'Credit Rating Report', type: 'pdf', url: '#' }
        ],
        targetAmount: 100000000,
        fundedAmount: 100000000,
        investorCount: 3421,
        issuer: { name: 'TechGiant Inc.', verified: true },
        launchDate: '2024-02-15',
        maturityDate: '2034-02-15',
        tags: ['bond', 'corporate', 'investment-grade', 'fixed-income']
    },
    {
        id: 'art-1',
        name: 'Contemporary Art Collection',
        symbol: 'ART-COL',
        type: 'art',
        price: 250.00,
        change24h: 0.0,
        description: 'Fractional ownership in a curated collection of contemporary artworks by emerging artists. The collection is managed by a renowned gallery and insured at full replacement value.',
        images: [
            'https://images.unsplash.com/photo-1561214115-f2f134cc4912?w=800',
            'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=800'
        ],
        riskRating: 4,
        term: 'Perpetual',
        minInvestment: 250,
        documents: [
            { name: 'Collection Catalog', type: 'pdf', url: '#' },
            { name: 'Insurance Certificate', type: 'pdf', url: '#' }
        ],
        investorCount: 156,
        issuer: { name: 'Blue Chip Gallery', verified: true },
        launchDate: '2024-04-01',
        tags: ['art', 'alternative', 'collectibles']
    },
    {
        id: 'green-bond-1',
        name: 'Green Energy Infrastructure',
        symbol: 'GREEN',
        type: 'bond',
        price: 50.00,
        change24h: 0.15,
        description: 'ESG-compliant green bonds financing solar and wind energy projects across Europe. Supports the transition to renewable energy while providing stable returns.',
        images: ['https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800'],
        apy: 4.25,
        riskRating: 2,
        term: '7 years',
        minInvestment: 200,
        documents: [
            { name: 'Green Bond Framework', type: 'pdf', url: '#' },
            { name: 'Impact Report', type: 'pdf', url: '#' }
        ],
        targetAmount: 75000000,
        fundedAmount: 68000000,
        investorCount: 2156,
        issuer: { name: 'EuroGreen Energy', verified: true },
        launchDate: '2024-05-01',
        maturityDate: '2031-05-01',
        tags: ['bond', 'green', 'esg', 'renewable-energy']
    }
];

// Generate 30 days of realistic portfolio history
const generatePortfolioHistory = (baseValue: number): { date: string; value: number }[] => {
    const history: { date: string; value: number }[] = [];
    let currentValue = baseValue * 0.85; // Start 15% lower
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    for (let i = 0; i < 30; i++) {
        const dayName = days[i % 7];
        const volatility = (Math.random() - 0.4) * 0.03; // Slightly upward bias
        currentValue = currentValue * (1 + volatility);
        history.push({
            date: i < 7 ? dayName : `Day ${i + 1}`,
            value: Math.round(currentValue)
        });
    }

    // Ensure last value is close to baseValue
    history[history.length - 1].value = baseValue;
    return history;
};

// --- MOCK USERS ---
const MOCK_ADMIN: User = {
    id: 'admin-1',
    name: 'Sarah Chen',
    email: 'admin@platform.com',
    role: 'admin',
    kycStatus: 'verified',
    joinedAt: '2023-01-15T00:00:00Z',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
    wallet: {
        address: '0xADMIN...9999',
        custodial: true,
        balances: { USD: 1000000, USDC: 500000, EUR: 250000 },
        pending: { USD: 0, USDC: 0, EUR: 0 }
    },
    preferences: {
        notifications: true,
        twoFactorEnabled: true,
        language: 'en',
        timezone: 'America/New_York'
    }
};

const MOCK_INVESTOR: User = {
    id: 'user-1',
    name: 'John Anderson',
    email: 'john@example.com',
    role: 'investor',
    kycStatus: 'verified',
    joinedAt: '2024-03-20T00:00:00Z',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
    portfolio: [
        { assetId: 'btc-1', amount: 0.45, avgCost: 58000, purchaseDate: '2024-06-15' },
        { assetId: 'eth-1', amount: 12.5, avgCost: 3200, purchaseDate: '2024-07-01' },
        { assetId: 'nyc-re-1', amount: 10, avgCost: 1000, purchaseDate: '2024-08-10' },
        { assetId: 'sgb-1', amount: 100, avgCost: 52, purchaseDate: '2024-09-01' },
        { assetId: 'miami-re-1', amount: 5, avgCost: 850, purchaseDate: '2024-10-15' }
    ],
    portfolioHistory: generatePortfolioHistory(92500),
    wallet: {
        address: '0x1234...5678',
        custodial: true,
        balances: { USD: 15400, USDC: 8500, EUR: 0 },
        pending: { USD: 0, USDC: 500, EUR: 0 }
    },
    preferences: {
        notifications: true,
        twoFactorEnabled: false,
        language: 'en',
        timezone: 'America/New_York'
    }
};

const MOCK_PENDING_USER: User = {
    id: 'user-2',
    name: 'Emma Wilson',
    email: 'emma@example.com',
    role: 'investor',
    kycStatus: 'pending',
    joinedAt: '2024-11-01T00:00:00Z',
    wallet: {
        address: '0xABCD...EFGH',
        custodial: true,
        balances: { USD: 5000, USDC: 0, EUR: 0 },
        pending: { USD: 0, USDC: 0, EUR: 0 }
    }
};

// --- INITIAL NOTIFICATIONS ---
const INITIAL_NOTIFICATIONS: Notification[] = [
    {
        id: 'notif-1',
        type: 'dividend',
        title: 'Dividend Received',
        message: 'You received $127.50 from NYC-RE quarterly distribution',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        read: false
    },
    {
        id: 'notif-2',
        type: 'price-alert',
        title: 'Bitcoin Price Alert',
        message: 'BTC has increased 5% in the last 24 hours',
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        read: false
    },
    {
        id: 'notif-3',
        type: 'system',
        title: 'New Asset Available',
        message: 'Green Energy Infrastructure bonds are now available for investment',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        read: true
    },
    {
        id: 'notif-4',
        type: 'transaction',
        title: 'Investment Confirmed',
        message: 'Your investment of $4,250 in Miami Beachfront Residences is confirmed',
        timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
        read: true
    }
];

// --- INITIAL TRANSACTIONS ---
const INITIAL_TRANSACTIONS: Transaction[] = [
    {
        id: 'tx-1',
        userId: 'user-1',
        type: 'deposit',
        amount: 25000,
        currency: 'USD',
        timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'completed',
        description: 'Bank wire transfer'
    },
    {
        id: 'tx-2',
        userId: 'user-1',
        type: 'invest',
        assetId: 'nyc-re-1',
        assetName: 'Manhattan Prime Office',
        amount: 10500,
        currency: 'USD',
        timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'completed',
        description: 'Purchased 10 NYC-RE tokens',
        txHash: '0x8f2e...3a1b'
    },
    {
        id: 'tx-3',
        userId: 'user-1',
        type: 'distribution',
        assetId: 'nyc-re-1',
        assetName: 'Manhattan Prime Office',
        amount: 127.50,
        currency: 'USDC',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        status: 'completed',
        description: 'Q4 2024 dividend distribution'
    },
    {
        id: 'tx-4',
        userId: 'user-1',
        type: 'invest',
        assetId: 'miami-re-1',
        assetName: 'Miami Beachfront Residences',
        amount: 4250,
        currency: 'USD',
        timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
        status: 'completed',
        description: 'Purchased 5 MIA-LUX tokens',
        txHash: '0x9c3d...4b2e'
    }
];

// --- STORE CREATION ---
const createInitialState = () => ({
    isAuthenticated: true,
    currentUser: MOCK_INVESTOR,
    assets: INITIAL_ASSETS,
    users: [MOCK_INVESTOR, MOCK_ADMIN, MOCK_PENDING_USER],
    transactions: INITIAL_TRANSACTIONS,
    notifications: INITIAL_NOTIFICATIONS,
    selectedAssetId: null as string | null
});

export const useRWAStore = create<RWAStore>()(
    persist(
        (set, get) => ({
            ...createInitialState(),

            login: (email) => {
                const user = get().users.find(u => u.email === email);
                if (user) {
                    set({ isAuthenticated: true, currentUser: user });
                }
            },

            register: (name, email, role) => {
                const newUser: User = {
                    id: `user-${Date.now()}`,
                    name,
                    email,
                    role,
                    kycStatus: 'none',
                    joinedAt: new Date().toISOString(),
                    wallet: {
                        address: `0x${Math.random().toString(16).substr(2, 8)}...${Math.random().toString(16).substr(2, 4)}`,
                        custodial: true,
                        balances: { USD: 0, USDC: 0, EUR: 0 },
                        pending: { USD: 0, USDC: 0, EUR: 0 }
                    },
                    preferences: {
                        notifications: true,
                        twoFactorEnabled: false,
                        language: 'en',
                        timezone: 'UTC'
                    }
                };
                set(state => ({
                    users: [...state.users, newUser],
                    currentUser: newUser,
                    isAuthenticated: true
                }));
            },

            logout: () => set({ isAuthenticated: false, currentUser: null }),

            submitKyc: () => {
                set(state => {
                    if (!state.currentUser) return {};
                    const updatedUser = { ...state.currentUser, kycStatus: 'pending' as KycStatus };
                    return {
                        currentUser: updatedUser,
                        users: state.users.map(u => u.id === updatedUser.id ? updatedUser : u)
                    };
                });
            },

            approveKyc: (userId) => {
                set(state => {
                    const updatedUsers = state.users.map(u =>
                        u.id === userId ? { ...u, kycStatus: 'verified' as KycStatus } : u
                    );
                    const updatedCurrentUser = state.currentUser?.id === userId
                        ? { ...state.currentUser, kycStatus: 'verified' as KycStatus }
                        : state.currentUser;

                    // Add notification
                    const notification: Notification = {
                        id: `notif-${Date.now()}`,
                        type: 'kyc',
                        title: 'KYC Approved',
                        message: 'Your identity verification has been approved',
                        timestamp: new Date().toISOString(),
                        read: false
                    };

                    return {
                        users: updatedUsers,
                        currentUser: updatedCurrentUser,
                        notifications: [notification, ...state.notifications]
                    };
                });
            },

            freezeUser: (userId) => {
                set(state => {
                    const updatedUsers = state.users.map(u =>
                        u.id === userId ? { ...u, kycStatus: 'frozen' as KycStatus } : u
                    );
                    const updatedCurrentUser = state.currentUser?.id === userId
                        ? { ...state.currentUser, kycStatus: 'frozen' as KycStatus }
                        : state.currentUser;
                    return { users: updatedUsers, currentUser: updatedCurrentUser };
                });
            },

            unfreezeUser: (userId) => {
                set(state => {
                    const updatedUsers = state.users.map(u =>
                        u.id === userId ? { ...u, kycStatus: 'verified' as KycStatus } : u
                    );
                    const updatedCurrentUser = state.currentUser?.id === userId
                        ? { ...state.currentUser, kycStatus: 'verified' as KycStatus }
                        : state.currentUser;
                    return { users: updatedUsers, currentUser: updatedCurrentUser };
                });
            },

            deposit: (amount, currency) => {
                set(state => {
                    if (!state.currentUser?.wallet) return {};

                    const newWallet = {
                        ...state.currentUser.wallet,
                        balances: {
                            ...state.currentUser.wallet.balances,
                            [currency]: state.currentUser.wallet.balances[currency] + amount
                        }
                    };
                    const updatedUser = { ...state.currentUser, wallet: newWallet };

                    const newTx: Transaction = {
                        id: `tx-${Date.now()}`,
                        userId: updatedUser.id,
                        type: 'deposit',
                        amount,
                        currency,
                        timestamp: new Date().toISOString(),
                        status: 'completed',
                        description: `${currency} deposit via bank transfer`
                    };

                    const notification: Notification = {
                        id: `notif-${Date.now()}`,
                        type: 'transaction',
                        title: 'Deposit Successful',
                        message: `$${amount.toLocaleString()} ${currency} has been added to your wallet`,
                        timestamp: new Date().toISOString(),
                        read: false
                    };

                    return {
                        currentUser: updatedUser,
                        users: state.users.map(u => u.id === updatedUser.id ? updatedUser : u),
                        transactions: [newTx, ...state.transactions],
                        notifications: [notification, ...state.notifications]
                    };
                });
            },

            withdraw: (amount, currency) => {
                set(state => {
                    if (!state.currentUser?.wallet) return {};
                    if (state.currentUser.wallet.balances[currency] < amount) return {};

                    const newWallet = {
                        ...state.currentUser.wallet,
                        balances: {
                            ...state.currentUser.wallet.balances,
                            [currency]: state.currentUser.wallet.balances[currency] - amount
                        }
                    };
                    const updatedUser = { ...state.currentUser, wallet: newWallet };

                    const newTx: Transaction = {
                        id: `tx-${Date.now()}`,
                        userId: updatedUser.id,
                        type: 'withdrawal',
                        amount,
                        currency,
                        timestamp: new Date().toISOString(),
                        status: 'pending',
                        description: `${currency} withdrawal requested`
                    };

                    return {
                        currentUser: updatedUser,
                        users: state.users.map(u => u.id === updatedUser.id ? updatedUser : u),
                        transactions: [newTx, ...state.transactions]
                    };
                });
            },

            addAsset: (asset) => set((state) => ({ assets: [...state.assets, asset] })),

            deleteAsset: (id) => set((state) => ({ assets: state.assets.filter(a => a.id !== id) })),

            updatePortfolio: (assetId, amount, cost) => {
                set(state => {
                    if (!state.currentUser) return {};

                    const asset = state.assets.find(a => a.id === assetId);
                    if (!asset) return {};

                    const existingItem = state.currentUser.portfolio?.find(p => p.assetId === assetId);
                    let newPortfolio: PortfolioItem[];

                    if (existingItem) {
                        // Update existing position
                        const totalAmount = existingItem.amount + amount;
                        const totalCost = (existingItem.avgCost * existingItem.amount) + (cost * amount);
                        const newAvgCost = totalCost / totalAmount;

                        newPortfolio = state.currentUser.portfolio!.map(p =>
                            p.assetId === assetId
                                ? { ...p, amount: totalAmount, avgCost: newAvgCost }
                                : p
                        );
                    } else {
                        // Add new position
                        newPortfolio = [
                            ...(state.currentUser.portfolio || []),
                            { assetId, amount, avgCost: cost, purchaseDate: new Date().toISOString().split('T')[0] }
                        ];
                    }

                    const updatedUser = { ...state.currentUser, portfolio: newPortfolio };

                    const newTx: Transaction = {
                        id: `tx-${Date.now()}`,
                        userId: state.currentUser.id,
                        type: 'invest',
                        assetId,
                        assetName: asset.name,
                        amount: cost * amount,
                        currency: 'USD',
                        timestamp: new Date().toISOString(),
                        status: 'completed',
                        description: `Purchased ${amount} ${asset.symbol} tokens`,
                        txHash: `0x${Math.random().toString(16).substr(2, 8)}...${Math.random().toString(16).substr(2, 4)}`
                    };

                    const notification: Notification = {
                        id: `notif-${Date.now()}`,
                        type: 'transaction',
                        title: 'Investment Confirmed',
                        message: `Your investment of $${(cost * amount).toLocaleString()} in ${asset.name} is confirmed`,
                        timestamp: new Date().toISOString(),
                        read: false
                    };

                    return {
                        currentUser: updatedUser,
                        users: state.users.map(u => u.id === updatedUser.id ? updatedUser : u),
                        transactions: [newTx, ...state.transactions],
                        notifications: [notification, ...state.notifications]
                    };
                });
            },

            distributeDividends: (assetId, totalAmount) => {
                set(state => {
                    const asset = state.assets.find(a => a.id === assetId);

                    const newTx: Transaction = {
                        id: `dist-${Date.now()}`,
                        userId: 'system',
                        type: 'distribution',
                        assetId,
                        assetName: asset?.name,
                        amount: totalAmount,
                        currency: 'USDC',
                        timestamp: new Date().toISOString(),
                        status: 'completed',
                        description: `Dividend distribution for ${asset?.symbol || 'Asset'}`
                    };

                    let updatedCurrentUser = state.currentUser;
                    if (state.currentUser?.wallet && state.currentUser.portfolio?.some(p => p.assetId === assetId)) {
                        const userShare = totalAmount / 100; // Simplified share calculation
                        const newWallet = {
                            ...state.currentUser.wallet,
                            balances: {
                                ...state.currentUser.wallet.balances,
                                USDC: state.currentUser.wallet.balances.USDC + userShare
                            }
                        };
                        updatedCurrentUser = { ...state.currentUser, wallet: newWallet };
                    }

                    const notification: Notification = {
                        id: `notif-${Date.now()}`,
                        type: 'dividend',
                        title: 'Dividend Received',
                        message: `You received a dividend from ${asset?.name || 'your investment'}`,
                        timestamp: new Date().toISOString(),
                        read: false
                    };

                    return {
                        transactions: [newTx, ...state.transactions],
                        currentUser: updatedCurrentUser,
                        notifications: [notification, ...state.notifications]
                    };
                });
            },

            selectAsset: (id) => set({ selectedAssetId: id }),

            addNotification: (notification) => {
                set(state => ({
                    notifications: [{
                        ...notification,
                        id: `notif-${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        read: false
                    }, ...state.notifications]
                }));
            },

            markNotificationRead: (id) => {
                set(state => ({
                    notifications: state.notifications.map(n =>
                        n.id === id ? { ...n, read: true } : n
                    )
                }));
            },

            markAllNotificationsRead: () => {
                set(state => ({
                    notifications: state.notifications.map(n => ({ ...n, read: true }))
                }));
            },

            clearNotifications: () => set({ notifications: [] }),

            switchUser: (userId) => {
                const user = get().users.find(u => u.id === userId);
                if (user) {
                    set({ currentUser: user, isAuthenticated: true });
                }
            },

            resetDemo: () => {
                set(createInitialState());
            }
        }),
        {
            name: 'rwa-enterprise-storage-v2',
        }
    )
);
